import { useEffect, useState } from "react";
import Dialog from "../../../components/Dialog/SideDrawer";
import { getCustomerOrders, debugOrders } from "../actions/customer.actions";
import { useAppSelector } from "../../../redux";
import { CUSTOMER_STORE_NAME } from "../../../contants/stores";
import { formatDate } from "../../../utils/helpers";

const CustomerOrdersDialog = (props: any) => {
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const [unsubscribe, setUnsubscribe] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const customerOrders = useAppSelector((state: any) => state[CUSTOMER_STORE_NAME].customerOrders);

  useEffect(() => {
    setOpen(true);
    if (props.customer?.id) {
      loadCustomerOrders();
    }
    
    // Cleanup function to unsubscribe from the listener
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [props.customer?.id]);

  const loadCustomerOrders = async () => {
    setLoading(true);
    try {
      // First, let's debug the orders structure to understand how customers are linked
      console.log("=== DEBUGGING FOR CUSTOMER ===", props.customer);
      await debugOrders();
      
      // Then try to get customer orders using the real-time listener
      const unsubscribeFn = getCustomerOrders(props.customer.id, props.customer.phone);
      setUnsubscribe(() => unsubscribeFn);
    } catch (error) {
      console.error('Error loading customer orders:', error);
    } finally {
      setLoading(false);
    }
  };

  function onClose(value: boolean) {
    setOpen(value);
    if (!value) {
      props.onClose();
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DELIVERED':
        return 'bg-green-100 text-green-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatAmount = (amount: number) => {
    return amount ? `${amount.toFixed(2)} KD` : '0.00 KD';
  };

  // Filter orders based on search query
  const filteredOrders = customerOrders?.filter((order: any) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    
    // Check delivery addresses (single or multi-stop)
    let addressMatch = false;
    if (order.isMultiStopOrder && order.deliveryStops) {
      addressMatch = order.deliveryStops.some((stop: any) => 
        stop.address && stop.address.toLowerCase().includes(query)
      );
    } else if (order.deliveryAddress && order.deliveryAddress.address) {
      addressMatch = order.deliveryAddress.address.toLowerCase().includes(query);
    }
    
    return (
      (order.orderNo && order.orderNo.toLowerCase().includes(query)) ||
      (order.primaryStatus && order.primaryStatus.toLowerCase().includes(query)) ||
      (order.orderType && order.orderType.toLowerCase().includes(query)) ||
      addressMatch ||
      (order.notes && order.notes.some((note: string) => note.toLowerCase().includes(query)))
    );
  }) || [];

  // Defensive date formatter
  const getOrderDate = (date: any) => {
    if (!date) return '';
    if (date.toDate) return formatDate(date.toDate());
    if (typeof date === 'string' || typeof date === 'number') {
      const d = new Date(date);
      return isNaN(d.getTime()) ? '' : formatDate(d);
    }
    return '';
  };

  return (
    <Dialog onClose={onClose} open={open} title={`Orders - ${props.customer?.name || 'Customer'}`} code={props.customer}>
      <div className="flow-root h-full flex flex-col">
        <div className="flex gap-2 flex-1 flex-col bg-white rounded-lg h-full w-full">
          {/* Customer Info Header */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="text-gray-600">
                Customer: <span className="font-semibold">{props.customer?.name}</span>
              </div>
              <div className="text-gray-600">
                Phone: <span className="font-semibold">{props.customer?.phone}</span>
              </div>
              <div className="text-gray-600">
                Total Orders: <span className="font-semibold">{customerOrders?.length || 0}</span>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search orders by order number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            {searchQuery && (
              <div className="mt-2 text-sm text-gray-600">
                Showing {filteredOrders.length} of {customerOrders?.length || 0} orders
              </div>
            )}
          </div>

          {/* Orders List */}
          <div className="flex-1 overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading orders...</span>
              </div>
            ) : filteredOrders && filteredOrders.length > 0 ? (
              <div className="overflow-y-auto h-full pr-2">
                <div className="space-y-3">
                  {filteredOrders.map((order: any) => (
                    <div key={order.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-gray-900">{order.orderNo}</span>
                            {order.isMultiStopOrder && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-purple-500 text-white">
                                Multi-Stop
                              </span>
                            )}
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.primaryStatus)}`}>
                              {order.primaryStatus}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            {order.orderType} • {order.itemCount || 0} items
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-semibold text-gray-900">
                            {formatAmount(order.orderInfo?.totalAmount)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {getOrderDate(order.placedAt) || 'N/A'}
                          </div>
                        </div>
                      </div>
                      
                      {/* Display delivery address or multi-stop */}
                      {order.isMultiStopOrder && order.deliveryStops && order.deliveryStops.length > 0 ? (
                        <div className="text-xs text-gray-500 mt-2">
                          <span className="font-medium">Delivery ({order.deliveryStops.length} stops):</span>
                          <div className="ml-2 mt-1 space-y-1">
                            {order.deliveryStops.map((stop: any, index: number) => (
                              <div key={index} className="flex items-start">
                                <span className="font-semibold mr-1">{index + 1}.</span>
                                <span>{stop.address || stop.addressStr || stop.name || 'N/A'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : order.deliveryAddress ? (
                        <div className="text-xs text-gray-500 mt-2">
                          <span className="font-medium">Delivery:</span> {order.deliveryAddress.address}
                        </div>
                      ) : null}
                      
                      {order.notes && order.notes.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          <span className="font-medium">Notes:</span> {order.notes.join(', ')}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                <svg className="w-12 h-12 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium">
                  {searchQuery ? 'No orders match your search' : 'No orders found'}
                </p>
                <p className="text-sm">
                  {searchQuery ? 'Try adjusting your search terms' : 'This customer hasn\'t placed any orders yet.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default CustomerOrdersDialog; 